import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const API = process.env.REACT_APP_API_URL;
const PER_PAGE = 50;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt$ = (val) =>
    val == null ? null : '$' + Number(val).toFixed(2);

const fmtPct = (val) =>
    val == null ? null : (val >= 0 ? '+' : '') + Number(val).toFixed(2) + '%';

const safeNum = (v) => (v === '' || v === null || v === undefined ? '' : String(v));

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatCard = ({ label, value, sub, accent }) => (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <p className="text-xs font-medium text-gray-500 truncate">{label}</p>
        <p className={`mt-1 text-2xl font-semibold ${accent || 'text-gray-800'}`}>
            {value == null ? <span className="text-gray-300">-</span> : value.toLocaleString()}
        </p>
        {sub && <p className="mt-0.5 text-xs text-gray-400">{sub}</p>}
    </div>
);

const PriceStatusBadge = ({ status }) => {
    if (!status) return <span className="text-xs text-gray-400 italic">no data</span>;
    const map = {
        success:         'bg-green-100 text-green-700',
        manual_override: 'bg-blue-100 text-blue-700',
        not_found:       'bg-yellow-100 text-yellow-700',
        error:           'bg-red-100 text-red-700',
        skipped:         'bg-gray-100 text-gray-500',
    };
    const cls = map[status] || 'bg-gray-100 text-gray-500';
    return (
        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
            {status.replace(/_/g, ' ')}
        </span>
    );
};

const SaveMsg = ({ msg }) => {
    if (!msg) return null;
    const isOk = msg.type === 'success';
    return (
        <div className={`mt-3 px-3 py-2 rounded text-sm ${isOk ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
            {msg.text}
        </div>
    );
};

const PriceInput = ({ label, name, value, onChange, hint }) => (
    <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
            {label}
            {hint && <span className="ml-1 font-normal text-gray-400">{hint}</span>}
        </label>
        <div className="relative">
            <span className="absolute inset-y-0 left-2.5 flex items-center text-gray-400 text-sm pointer-events-none">$</span>
            <input
                type="number"
                step="0.01"
                min="0"
                name={name}
                value={value}
                onChange={onChange}
                placeholder="-"
                className="w-full pl-6 pr-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-400 focus:border-red-400"
            />
        </div>
    </div>
);

// ─── Edit Slide-over ──────────────────────────────────────────────────────────

const EditPanel = ({ set, onClose, onSetSaved, onPriceSaved }) => {
    const [setForm, setSetForm] = useState({
        name: set.name || '',
        year: safeNum(set.year),
        num_parts: safeNum(set.num_parts),
    });
    const [priceForm, setPriceForm] = useState({
        retail_price:            safeNum(set.retail_price),
        market_price:            safeNum(set.market_price),
        market_price_difference: safeNum(set.market_price_difference),
        sealed_value:            safeNum(set.sealed_value),
        used_value:              safeNum(set.used_value),
        used_value_range_low:    safeNum(set.used_value_range_low),
        used_value_range_high:   safeNum(set.used_value_range_high),
        status:                  set.price_status || 'manual_override',
        status_message:          set.price_status_message || '',
    });

    const [savingSet, setSavingSet]     = useState(false);
    const [savingPrice, setSavingPrice] = useState(false);
    const [setMsg, setSetMsg]           = useState(null);
    const [priceMsg, setPriceMsg]       = useState(null);

    const themeLine = [set.parent_theme_name, set.theme_name]
        .filter(Boolean)
        .join(' › ');

    const handleSetChange = (e) =>
        setSetForm((p) => ({ ...p, [e.target.name]: e.target.value }));

    const handlePriceChange = (e) =>
        setPriceForm((p) => ({ ...p, [e.target.name]: e.target.value }));

    const saveSet = async () => {
        setSavingSet(true);
        setSetMsg(null);
        try {
            const res = await fetch(`${API}/admin_sets_save.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    set_num:   set.set_num,
                    name:      setForm.name,
                    year:      setForm.year !== '' ? parseInt(setForm.year, 10) : null,
                    num_parts: setForm.num_parts !== '' ? parseInt(setForm.num_parts, 10) : null,
                }),
            });
            const data = await res.json();
            if (data.success) {
                setSetMsg({ type: 'success', text: 'Set details saved.' });
                onSetSaved(set.set_num, {
                    name:      setForm.name,
                    year:      setForm.year !== '' ? parseInt(setForm.year, 10) : null,
                    num_parts: setForm.num_parts !== '' ? parseInt(setForm.num_parts, 10) : null,
                });
            } else {
                setSetMsg({ type: 'error', text: data.error || 'Save failed.' });
            }
        } catch {
            setSetMsg({ type: 'error', text: 'Network error. Try again.' });
        } finally {
            setSavingSet(false);
        }
    };

    const savePrice = async () => {
        setSavingPrice(true);
        setPriceMsg(null);
        try {
            const res = await fetch(`${API}/admin_sets_save_price.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ set_num: set.set_num, ...priceForm }),
            });
            const data = await res.json();
            if (data.success) {
                setPriceMsg({ type: 'success', text: 'Price data saved.' });
                onPriceSaved(set.set_num, { ...priceForm });
            } else {
                setPriceMsg({ type: 'error', text: data.error || 'Save failed.' });
            }
        } catch {
            setPriceMsg({ type: 'error', text: 'Network error. Try again.' });
        } finally {
            setSavingPrice(false);
        }
    };

    return (
        <div className="fixed inset-0 z-40 flex justify-end">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black bg-opacity-40"
                onClick={onClose}
            />

            {/* Panel */}
            <div className="relative z-50 w-full max-w-md bg-white shadow-2xl flex flex-col h-full overflow-hidden">
                {/* Header */}
                <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between bg-gray-50">
                    <div className="min-w-0 flex-1 pr-4">
                        <p className="text-xs font-mono text-gray-400 uppercase tracking-wider mb-0.5">{set.set_num}</p>
                        <h2 className="text-base font-semibold text-gray-900 leading-snug line-clamp-2">{set.name}</h2>
                        {themeLine && (
                            <p className="mt-0.5 text-xs text-gray-500">{themeLine} · {set.year}</p>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="flex-shrink-0 p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                        aria-label="Close"
                    >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Scrollable body */}
                <div className="flex-1 overflow-y-auto px-5 py-5 space-y-7">

                    {/* ── SET DETAILS ────────────────────────────── */}
                    <section>
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                            Set Details
                        </h3>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={setForm.name}
                                    onChange={handleSetChange}
                                    maxLength={256}
                                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-400 focus:border-red-400"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Year</label>
                                    <input
                                        type="number"
                                        name="year"
                                        value={setForm.year}
                                        onChange={handleSetChange}
                                        min={1949}
                                        max={2099}
                                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-400 focus:border-red-400"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                        Pieces
                                        {(set.num_parts === 0 || set.num_parts === null) && (
                                            <span className="ml-1 text-orange-500">⚠</span>
                                        )}
                                    </label>
                                    <input
                                        type="number"
                                        name="num_parts"
                                        value={setForm.num_parts}
                                        onChange={handleSetChange}
                                        min={0}
                                        className={`w-full px-3 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-red-400 focus:border-red-400 ${
                                            (parseInt(setForm.num_parts, 10) === 0 || setForm.num_parts === '')
                                                ? 'border-orange-300 bg-orange-50'
                                                : 'border-gray-300'
                                        }`}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Theme</label>
                                <p className="text-sm text-gray-500 bg-gray-50 px-3 py-1.5 rounded border border-gray-200">
                                    {themeLine || <span className="italic text-gray-400">Unthemed</span>}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={saveSet}
                            disabled={savingSet}
                            className="mt-4 w-full flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {savingSet ? (
                                <>
                                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                                    Saving…
                                </>
                            ) : 'Save Set Details'}
                        </button>
                        <SaveMsg msg={setMsg} />
                    </section>

                    <div className="border-t border-gray-100" />

                    {/* ── PRICE DATA ─────────────────────────────── */}
                    <section>
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                            Price Data
                        </h3>
                        <p className="text-xs text-gray-400 mb-3">
                            These fields are normally populated by the price scraper. You can manually set or correct them here. Leave a field blank to store <span className="font-mono">NULL</span>.
                        </p>

                        <div className="grid grid-cols-2 gap-3">
                            <PriceInput label="Retail Price"   name="retail_price"   value={priceForm.retail_price}   onChange={handlePriceChange} />
                            <PriceInput label="Market Price"   name="market_price"   value={priceForm.market_price}   onChange={handlePriceChange} />
                            <PriceInput label="Sealed Value"   name="sealed_value"   value={priceForm.sealed_value}   onChange={handlePriceChange} />
                            <PriceInput label="Used Value"     name="used_value"     value={priceForm.used_value}     onChange={handlePriceChange} />
                            <PriceInput label="Used Range Low" name="used_value_range_low"  value={priceForm.used_value_range_low}  onChange={handlePriceChange} />
                            <PriceInput label="Used Range High" name="used_value_range_high" value={priceForm.used_value_range_high} onChange={handlePriceChange} />
                        </div>

                        {/* Market price difference - can be negative, no $ prefix */}
                        <div className="mt-3">
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                Market % Diff
                                <span className="ml-1 font-normal text-gray-400">(market vs retail, can be negative)</span>
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                name="market_price_difference"
                                value={priceForm.market_price_difference}
                                onChange={handlePriceChange}
                                placeholder="-"
                                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-400 focus:border-red-400"
                            />
                        </div>

                        {/* Status fields */}
                        <div className="mt-3 grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                                <select
                                    name="status"
                                    value={priceForm.status}
                                    onChange={handlePriceChange}
                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-400 focus:border-red-400 bg-white"
                                >
                                    <option value="manual_override">manual_override</option>
                                    <option value="success">success</option>
                                    <option value="not_found">not_found</option>
                                    <option value="error">error</option>
                                    <option value="skipped">skipped</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Last Updated</label>
                                <p className="text-xs text-gray-500 py-2">
                                    {set.price_last_updated
                                        ? new Date(set.price_last_updated).toLocaleString()
                                        : <span className="italic text-gray-400">never</span>}
                                </p>
                            </div>
                        </div>
                        <div className="mt-3">
                            <label className="block text-xs font-medium text-gray-600 mb-1">Status Message</label>
                            <textarea
                                name="status_message"
                                value={priceForm.status_message}
                                onChange={handlePriceChange}
                                rows={2}
                                placeholder="Scraper note or manual note…"
                                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-400 focus:border-red-400 resize-none"
                            />
                        </div>

                        <button
                            onClick={savePrice}
                            disabled={savingPrice}
                            className="mt-4 w-full flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {savingPrice ? (
                                <>
                                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                                    Saving…
                                </>
                            ) : 'Save Price Data'}
                        </button>
                        <SaveMsg msg={priceMsg} />
                    </section>

                    {/* ── SCRAPER STATUS (read-only info) ─────────── */}
                    <section>
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                            Scraper Info
                        </h3>
                        <div className="bg-gray-50 rounded border border-gray-100 px-4 py-3 space-y-2 text-sm">
                            <div className="flex items-center gap-2">
                                <span className="text-gray-500 w-28 flex-shrink-0 text-xs">Status</span>
                                <PriceStatusBadge status={set.price_status} />
                            </div>
                            <div className="flex items-start gap-2">
                                <span className="text-gray-500 w-28 flex-shrink-0 text-xs">Message</span>
                                <span className="text-gray-700 text-xs break-words">
                                    {set.price_status_message || <span className="italic text-gray-400">none</span>}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-gray-500 w-28 flex-shrink-0 text-xs">Last Updated</span>
                                <span className="text-gray-700 text-xs">
                                    {set.price_last_updated
                                        ? new Date(set.price_last_updated).toLocaleString()
                                        : <span className="italic text-gray-400">never</span>}
                                </span>
                            </div>
                            {(set.retail_price || set.market_price) && (
                                <div className="pt-2 border-t border-gray-200 grid grid-cols-2 gap-x-4 gap-y-1">
                                    {[
                                        ['Retail',      fmt$(set.retail_price)],
                                        ['Market',      fmt$(set.market_price)],
                                        ['Mkt % Diff',  fmtPct(set.market_price_difference)],
                                        ['Sealed',      fmt$(set.sealed_value)],
                                        ['Used',        fmt$(set.used_value)],
                                        ['Used Low',    fmt$(set.used_value_range_low)],
                                        ['Used High',   fmt$(set.used_value_range_high)],
                                    ].map(([k, v]) => v && (
                                        <div key={k} className="flex gap-1 text-xs">
                                            <span className="text-gray-400 w-16 flex-shrink-0">{k}</span>
                                            <span className="font-medium text-gray-700">{v}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

// ─── Results Table Row ────────────────────────────────────────────────────────

const SetRow = ({ set, onEdit }) => {
    const missingPieces = set.num_parts === 0 || set.num_parts === null;
    const hasPrice      = set.retail_price != null;
    const themeLine     = [set.parent_theme_name, set.theme_name].filter(Boolean).join(' › ');

    return (
        <tr className="hover:bg-gray-50 transition-colors">
            <td className="px-4 py-3 text-xs font-mono text-gray-500 whitespace-nowrap">{set.set_num}</td>
            <td className="px-4 py-3 text-sm text-gray-800 max-w-xs">
                <span className="line-clamp-2 leading-snug">{set.name}</span>
            </td>
            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{set.year ?? '-'}</td>
            <td className="px-4 py-3 text-xs text-gray-500 max-w-[160px]">
                <span className="line-clamp-2">{themeLine || <span className="italic text-gray-300">-</span>}</span>
            </td>
            <td className="px-4 py-3 text-sm whitespace-nowrap">
                {missingPieces ? (
                    <span className="inline-flex items-center gap-1 text-orange-600 font-medium">
                        <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {set.num_parts === null ? 'null' : '0'}
                    </span>
                ) : (
                    <span className="text-gray-700">{set.num_parts.toLocaleString()}</span>
                )}
            </td>
            <td className="px-4 py-3 text-sm whitespace-nowrap">
                {hasPrice ? (
                    <span className="text-gray-800 font-medium">{fmt$(set.retail_price)}</span>
                ) : (
                    <span className="text-gray-400 italic text-xs">no price</span>
                )}
            </td>
            <td className="px-4 py-3">
                <PriceStatusBadge status={set.price_status} />
            </td>
            <td className="px-4 py-3 text-right">
                <button
                    onClick={() => onEdit(set)}
                    className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded border border-gray-300 text-gray-600 hover:bg-gray-100 hover:border-gray-400 transition-colors"
                >
                    Edit
                </button>
            </td>
        </tr>
    );
};

// ─── Pagination ───────────────────────────────────────────────────────────────

const Pagination = ({ page, totalPages, totalCount, loading, onPage }) => {
    if (totalPages <= 1) return null;
    const start = (page - 1) * PER_PAGE + 1;
    const end   = Math.min(page * PER_PAGE, totalCount);

    return (
        <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
            <p className="text-xs text-gray-500">
                {loading ? 'Loading…' : `Showing ${start.toLocaleString()}–${end.toLocaleString()} of ${totalCount.toLocaleString()}`}
            </p>
            <div className="flex items-center gap-1">
                <button
                    onClick={() => onPage(page - 1)}
                    disabled={page <= 1 || loading}
                    className="px-2.5 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    ‹ Prev
                </button>
                <span className="px-3 py-1 text-xs text-gray-600">
                    {page} / {totalPages}
                </span>
                <button
                    onClick={() => onPage(page + 1)}
                    disabled={page >= totalPages || loading}
                    className="px-2.5 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    Next ›
                </button>
            </div>
        </div>
    );
};

// ─── Filter Tabs ──────────────────────────────────────────────────────────────

const FILTERS = [
    { key: 'zero_pieces',    label: 'Missing Pieces',   statKey: 'zero_pieces' },
    { key: 'no_price',       label: 'No Price Data',    statKey: 'no_price' },
    { key: 'scraper_errors', label: 'Scraper Errors',   statKey: 'scraper_errors' },
    { key: 'all',            label: 'All Sets',         statKey: 'total_sets' },
];

// ─── Main Component ───────────────────────────────────────────────────────────

const AdminSets = () => {
    const { user, isLoading: authLoading, checkAdminStatus } = useAuth();
    const navigate = useNavigate();
    useDocumentTitle('Admin Sets');

    const [isLoading,    setIsLoading]    = useState(true);
    const [isAuthorized, setIsAuthorized] = useState(false);

    // Stats
    const [stats,        setStats]        = useState(null);
    const [statsLoading, setStatsLoading] = useState(true);

    // Search / filter / pagination
    const [filter,         setFilter]         = useState('zero_pieces');
    const [searchInput,    setSearchInput]    = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [page,           setPage]           = useState(1);

    // Results
    const [results,      setResults]      = useState([]);
    const [totalCount,   setTotalCount]   = useState(0);
    const [totalPages,   setTotalPages]   = useState(1);
    const [resultsLoading, setResultsLoading] = useState(false);
    const [resultsError,   setResultsError]   = useState(null);

    // Edit panel
    const [editSet, setEditSet] = useState(null);

    const debounceRef = useRef(null);

    // ── Auth ──────────────────────────────────────────────────────────────────
    useEffect(() => {
        const verify = async () => {
            if (authLoading) return;
            if (!user) { navigate('/'); return; }
            const ok = await checkAdminStatus();
            if (!ok) { navigate('/'); return; }
            setIsAuthorized(true);
            setIsLoading(false);
        };
        verify();
    }, [user, authLoading, checkAdminStatus, navigate]);

    // ── Stats ────────────────────────────────────────────────────────────────
    const loadStats = useCallback(async () => {
        setStatsLoading(true);
        try {
            const res  = await fetch(`${API}/admin_sets_stats.php`, { credentials: 'include' });
            const data = await res.json();
            if (data.success) setStats(data.stats);
        } catch (e) {
            console.error('Failed to load stats', e);
        } finally {
            setStatsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isAuthorized) loadStats();
    }, [isAuthorized, loadStats]);

    // ── Debounce search input ─────────────────────────────────────────────────
    useEffect(() => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setDebouncedQuery(searchInput);
            setPage(1);
        }, 380);
        return () => clearTimeout(debounceRef.current);
    }, [searchInput]);

    // ── Load results ─────────────────────────────────────────────────────────
    const loadResults = useCallback(async () => {
        setResultsLoading(true);
        setResultsError(null);
        try {
            const params = new URLSearchParams({
                filter,
                page: String(page),
            });
            if (debouncedQuery) params.set('q', debouncedQuery);

            const res  = await fetch(`${API}/admin_sets_search.php?${params}`, { credentials: 'include' });
            const data = await res.json();
            if (data.success) {
                setResults(data.sets);
                setTotalCount(data.pagination.total_count);
                setTotalPages(data.pagination.total_pages);
            } else {
                setResultsError(data.error || 'Failed to load sets.');
            }
        } catch (e) {
            setResultsError('Network error. Try again.');
        } finally {
            setResultsLoading(false);
        }
    }, [filter, page, debouncedQuery]);

    useEffect(() => {
        if (isAuthorized) loadResults();
    }, [isAuthorized, loadResults]);

    // Reset page when filter changes
    const handleFilterChange = (f) => {
        setFilter(f);
        setPage(1);
    };

    // ── Edit callbacks ────────────────────────────────────────────────────────
    const handleSetSaved = useCallback((setNum, updates) => {
        setResults((prev) =>
            prev.map((s) => s.set_num === setNum ? { ...s, ...updates } : s)
        );
        // If editing the current set, merge updates in so re-open shows fresh
        setEditSet((prev) => prev && prev.set_num === setNum ? { ...prev, ...updates } : prev);
        loadStats();
    }, [loadStats]);

    const handlePriceSaved = useCallback((setNum, priceUpdates) => {
        const mapped = {
            retail_price:            priceUpdates.retail_price            !== '' ? parseFloat(priceUpdates.retail_price)            : null,
            market_price:            priceUpdates.market_price            !== '' ? parseFloat(priceUpdates.market_price)            : null,
            market_price_difference: priceUpdates.market_price_difference !== '' ? parseFloat(priceUpdates.market_price_difference) : null,
            sealed_value:            priceUpdates.sealed_value            !== '' ? parseFloat(priceUpdates.sealed_value)            : null,
            used_value:              priceUpdates.used_value              !== '' ? parseFloat(priceUpdates.used_value)              : null,
            used_value_range_low:    priceUpdates.used_value_range_low    !== '' ? parseFloat(priceUpdates.used_value_range_low)    : null,
            used_value_range_high:   priceUpdates.used_value_range_high   !== '' ? parseFloat(priceUpdates.used_value_range_high)   : null,
            price_status:            priceUpdates.status,
            price_status_message:    priceUpdates.status_message || null,
            price_last_updated:      new Date().toISOString(),
        };
        setResults((prev) =>
            prev.map((s) => s.set_num === setNum ? { ...s, ...mapped } : s)
        );
        setEditSet((prev) => prev && prev.set_num === setNum ? { ...prev, ...mapped } : prev);
        loadStats();
    }, [loadStats]);

    // ── Loading / auth gates ──────────────────────────────────────────────────
    if (isLoading || authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4" />
                    <p className="text-gray-500">Verifying admin access…</p>
                </div>
            </div>
        );
    }

    if (!isAuthorized) return null;

    const activeFilterMeta = FILTERS.find((f) => f.key === filter);

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                {/* Breadcrumb */}
                <nav className="flex mb-6" aria-label="Breadcrumb">
                    <ol className="inline-flex items-center space-x-1 md:space-x-3">
                        <li className="inline-flex items-center">
                            <Link to="/admin" className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-red-600">
                                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                                </svg>
                                Admin Dashboard
                            </Link>
                        </li>
                        <li>
                            <div className="flex items-center">
                                <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                </svg>
                                <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2">Set Management</span>
                            </div>
                        </li>
                    </ol>
                </nav>

                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">Set Management</h1>
                    <p className="mt-1 text-gray-500">
                        Browse, search, and edit LEGO set data and pricing. Images are not loaded in list view.
                    </p>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-8 mb-6">
                    <div className="col-span-2 sm:col-span-2">
                        <StatCard label="Total Sets"    value={stats?.total_sets}    sub={`${stats?.total_themes ?? '-'} themes`} />
                    </div>
                    <div className="col-span-2 sm:col-span-2">
                        <StatCard label="Sets with Price" value={stats?.sets_with_price} sub="retail price set" accent="text-green-700" />
                    </div>
                    <div className="col-span-2 sm:col-span-2">
                        <StatCard label="Missing Pieces" value={stats?.zero_pieces} sub="num_parts = 0 or null" accent={stats?.zero_pieces > 0 ? 'text-orange-600' : 'text-gray-800'} />
                    </div>
                    <div className="col-span-2 sm:col-span-2">
                        <StatCard label="No Price Data"  value={stats?.no_price}     sub="retail price missing" accent={stats?.no_price > 0 ? 'text-red-600' : 'text-gray-800'} />
                    </div>
                </div>

                {/* Search + Filter bar */}
                <div className="bg-white border border-gray-200 shadow-sm rounded-lg mb-4">
                    <div className="px-4 py-3 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center gap-3">
                        {/* Search */}
                        <div className="relative flex-1">
                            <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input
                                type="text"
                                value={searchInput}
                                onChange={(e) => { setSearchInput(e.target.value); setPage(1); }}
                                placeholder="Search by set number or name…"
                                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-400 focus:border-red-400"
                            />
                            {searchInput && (
                                <button
                                    onClick={() => { setSearchInput(''); setPage(1); }}
                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            )}
                        </div>

                        {/* Filter tabs */}
                        <div className="flex items-center gap-1 flex-shrink-0">
                            {FILTERS.map((f) => {
                                const count = stats?.[f.statKey];
                                const active = filter === f.key;
                                return (
                                    <button
                                        key={f.key}
                                        onClick={() => handleFilterChange(f.key)}
                                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors whitespace-nowrap ${
                                            active
                                                ? 'bg-red-600 text-white'
                                                : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
                                        }`}
                                    >
                                        {f.label}
                                        {count != null && (
                                            <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs ${active ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
                                                {count.toLocaleString()}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Results header */}
                    <div className="px-4 py-2 flex items-center justify-between border-b border-gray-100 bg-gray-50">
                        <p className="text-xs text-gray-500">
                            {resultsLoading
                                ? 'Loading…'
                                : debouncedQuery
                                    ? `${totalCount.toLocaleString()} result${totalCount !== 1 ? 's' : ''} matching "${debouncedQuery}" in ${activeFilterMeta?.label}`
                                    : `${totalCount.toLocaleString()} sets - ${activeFilterMeta?.label}`}
                        </p>
                        {resultsError && (
                            <p className="text-xs text-red-600">{resultsError}</p>
                        )}
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-100">
                            <thead className="bg-gray-50">
                                <tr>
                                    {['Set #', 'Name', 'Year', 'Theme', 'Pieces', 'Retail', 'Price Status', ''].map((h) => (
                                        <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 bg-white">
                                {resultsLoading ? (
                                    <tr>
                                        <td colSpan={8} className="py-16 text-center">
                                            <div className="flex flex-col items-center gap-3">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500" />
                                                <p className="text-sm text-gray-400">Loading sets…</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : results.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} className="py-14 text-center">
                                            <svg className="mx-auto h-8 w-8 text-gray-300 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            </svg>
                                            <p className="text-sm text-gray-500">No sets match the current filter.</p>
                                        </td>
                                    </tr>
                                ) : (
                                    results.map((set) => (
                                        <SetRow
                                            key={set.set_num}
                                            set={set}
                                            onEdit={setEditSet}
                                        />
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    <Pagination
                        page={page}
                        totalPages={totalPages}
                        totalCount={totalCount}
                        loading={resultsLoading}
                        onPage={setPage}
                    />
                </div>

                {/* Info banner */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-start gap-3">
                    <svg className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <p className="text-xs text-blue-700">
                        <strong>Price scraper note:</strong> Prices are automatically populated by the background scraper. Use the price editor to fix incorrect values or add prices the scraper missed. Saving price data sets the status to <span className="font-mono">manual_override</span> by default; the scraper will still update records it finds later, so you may want to set status to <span className="font-mono">success</span> to suppress future scraper re-runs on that set if your data is correct.
                    </p>
                </div>
            </div>

            {/* Edit Slide-over */}
            {editSet && (
                <EditPanel
                    set={editSet}
                    onClose={() => setEditSet(null)}
                    onSetSaved={handleSetSaved}
                    onPriceSaved={handlePriceSaved}
                />
            )}
        </div>
    );
};

export default AdminSets;
